import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { BookingChargeType, Currency } from "@/gql/graphql";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTranslations } from "use-intl";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { BookingRecord } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { Text } from "@/components/catalyst/text";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { DollarSignIcon } from "lucide-react";

export function PaymentSummarySection({ booking }: { booking: BookingRecord }) {
  const t = useTranslations("relocation");

  const hireUnitLabel = t(`hire_unit_type.${booking.hire_unit_type}`);

  const charges = booking.charges;
  const relocationCharge = charges.find(
    (c) => c.type === BookingChargeType.RelocationRate,
  );
  const dailyRateCharge = charges.find(
    (c) => c.type === BookingChargeType.DailyRate,
  );
  const bookingFeeCharge = charges.find(
    (c) => c.type === BookingChargeType.BookingFee,
  );

  const supplierDeposit = charges.find(
    (c) => c.type === BookingChargeType.SupplierRefundDeposit,
  );

  const platformDeposit = charges.find(
    (c) => c.type === BookingChargeType.PlatformHeldDeposit,
  );

  const additionalCharge = charges.find(
    (c) => c.type === BookingChargeType.AdditionalCharge,
  );

  const purchasableExtraCharges = booking.charges.filter(
    (c) => c.type === BookingChargeType.PurchasableExtra && c.quantity > 0,
  );

  if (booking.charges.length === 0) {
    return (
      <EmptyState
        Icon={DollarSignIcon}
        header="Charges"
        subheader="No charges found for this booking."
      />
    );
  }

  return (
    <table className="min-w-full sm:ml-auto sm:w-auto sm:max-w-2xl">
      <colgroup>
        <col className="w-full sm:w-2/3" />
        <col className="sm:w-1/3" />
      </colgroup>
      <thead className="border-b border-gray-300 text-gray-900">
        <tr>
          <th
            scope="col"
            className="py-3.5 pl-0 pr-3 text-left text-sm font-semibold text-gray-900"
          >
            Charge
          </th>
          <th
            scope="col"
            className="py-3.5 pl-3 pr-0 text-right text-sm font-semibold text-gray-900"
          >
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        {relocationCharge && relocationCharge.amount > 0 ? (
          <tr className="border-b border-gray-200">
            <td className="py-4 pl-0 pr-3 text-sm">
              <div className="font-medium text-gray-900">
                Relocation {hireUnitLabel}s
              </div>
              <div className="mt-0.5 text-gray-500">
                {booking.discounted_units} x{" "}
                {formatCurrency(relocationCharge.unit_price, booking.currency)}
              </div>
            </td>
            <td className="py-4 pl-3 pr-0 text-right text-sm text-gray-900">
              {formatCurrency(relocationCharge.amount, booking.currency)}
            </td>
          </tr>
        ) : null}

        {dailyRateCharge && dailyRateCharge.amount > 0 ? (
          <tr className="border-b border-gray-200">
            <td className="py-4 pl-0 pr-3 text-sm">
              <div className="font-medium text-gray-900">
                Extra {hireUnitLabel}s
              </div>
              <div className="mt-0.5 text-gray-500">
                {dailyRateCharge.quantity} x{" "}
                {formatCurrency(dailyRateCharge.unit_price, booking.currency)}
              </div>
            </td>
            <td className="py-4 pl-3 pr-0 text-right text-sm text-gray-900">
              {formatCurrency(dailyRateCharge.amount, booking.currency)}
            </td>
          </tr>
        ) : null}

        {bookingFeeCharge && bookingFeeCharge.amount > 0 ? (
          <tr className="border-b border-gray-200">
            <td className="py-4 pl-0 pr-3 text-sm">
              <div className="font-medium text-gray-900">Booking fee</div>
            </td>
            <td className="py-4 pl-3 pr-0 text-right text-sm text-gray-900">
              {formatCurrency(bookingFeeCharge.amount, booking.currency)}
            </td>
          </tr>
        ) : null}

        {platformDeposit && platformDeposit.platform_amount_collectable > 0 ? (
          <tr className="border-b border-gray-200">
            <td className="py-4 pl-0 pr-3 text-sm">
              <div className="font-medium text-gray-900">
                <Popover>
                  <PopoverTrigger
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-gray-600"
                  >
                    Holding deposit (Imoova Held)
                    <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="start" className="p-3">
                    <Text className="text-xs">
                      This is a deposit paid to Imoova to secure the booking.
                    </Text>
                  </PopoverContent>
                </Popover>
              </div>
            </td>
            <td className="py-4 pl-3 pr-0 text-right text-sm text-gray-900">
              {formatCurrency(
                platformDeposit.platform_amount_collectable,
                booking.currency,
              )}
            </td>
          </tr>
        ) : null}

        {supplierDeposit && supplierDeposit.platform_amount_collectable > 0 ? (
          <tr className="border-b border-gray-200">
            <td className="py-4 pl-0 pr-3 text-sm">
              <div className="font-medium text-gray-900">
                <Popover>
                  <PopoverTrigger
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-gray-600"
                  >
                    Holding deposit (Supplier refunded)
                    <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="start" className="p-3">
                    <Text className="text-xs">
                      This is a deposit paid to Imoova and refunded by the
                      supplier at pickup.
                    </Text>
                  </PopoverContent>
                </Popover>
              </div>
            </td>
            <td className="py-4 pl-3 pr-0 text-right text-sm text-gray-900">
              {formatCurrency(
                supplierDeposit.platform_amount_collectable,
                booking.currency,
              )}
            </td>
          </tr>
        ) : null}

        {additionalCharge && additionalCharge.amount > 0 ? (
          <tr className="border-b border-gray-200">
            <td className="py-4 pl-0 pr-3 text-sm">
              <div className="font-medium text-gray-900">Additional charge</div>
            </td>
            <td className="py-4 pl-3 pr-0 text-right text-sm text-gray-900">
              {formatCurrency(
                additionalCharge.supplier_amount_collectable,
                booking.currency,
              )}
            </td>
          </tr>
        ) : null}

        {purchasableExtraCharges.map((extra) => {
          if (extra.amount <= 0) return null;
          return (
            <tr key={extra.id} className="border-b border-gray-200">
              <td className="py-4 pl-0 pr-3 text-sm">
                <div className="font-medium text-gray-900">
                  {extra.purchasableExtra?.supplierPurchasableExtra.name}
                </div>
                <div className="mt-0.5 text-gray-500">
                  {extra.quantity} x{" "}
                  {formatCurrency(
                    extra.supplier_unit_price_collectable,
                    booking.currency,
                  )}
                </div>
              </td>
              <td className="py-4 pl-3 pr-0 text-right text-sm text-gray-900">
                {formatCurrency(extra.amount, booking.currency)}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <th
            scope="row"
            className="pl-0 pr-3 pt-6 text-right text-sm font-normal text-gray-500"
          >
            Pay now:
          </th>
          <td className="pl-3 pr-0 pt-6 text-right text-sm text-gray-500">
            <Popover>
              <PopoverTrigger
                type="button"
                className="inline-flex items-center gap-1 hover:text-gray-600"
              >
                {formatCurrency(
                  booking.charge_summary.pay_now_total_amount,
                  booking.currency,
                )}
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </PopoverTrigger>
              <PopoverContent side="right" align="start" className="p-3">
                <div className="w-60 space-y-2">
                  <TooltipLineItem
                    amount={
                      booking.charge_summary.pay_now_daily_rate_deposit_amount
                    }
                    currency={booking.currency}
                    label={`Extra ${hireUnitLabel} down payment`}
                  />
                  <TooltipLineItem
                    amount={booking.charge_summary.pay_now_booking_fee_amount}
                    currency={booking.currency}
                    label={`Booking fee`}
                  />
                  <TooltipLineItem
                    amount={
                      booking.charge_summary.pay_now_holding_deposit_amount
                    }
                    currency={booking.currency}
                    label={`Holding deposit`}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </td>
        </tr>
        <tr>
          <th
            scope="row"
            className="pl-0 pr-3 pt-4 text-right text-sm font-normal text-gray-500"
          >
            Pay later:
          </th>
          <td className="pl-3 pr-0 pt-4 text-right text-sm text-gray-500">
            <Popover>
              <PopoverTrigger
                type="button"
                className="inline-flex items-center gap-1 hover:text-gray-600"
              >
                {formatCurrency(
                  booking.charge_summary.pay_later_total_amount,
                  booking.currency,
                )}
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </PopoverTrigger>
              <PopoverContent side="right" align="start" className="p-3">
                <div className="w-60 space-y-2">
                  <TooltipLineItem
                    amount={
                      booking.charge_summary.pay_later_relocation_rate_amount
                    }
                    currency={booking.currency}
                    label={`Relocation ${hireUnitLabel}s`}
                  />
                  <TooltipLineItem
                    amount={booking.charge_summary.pay_later_daily_rate_amount}
                    currency={booking.currency}
                    label={`Extra ${hireUnitLabel}s`}
                  />
                  {booking.charges
                    .filter(
                      (c) => c.type === BookingChargeType.AdditionalCharge,
                    )
                    .map((charge) => {
                      return (
                        <TooltipLineItem
                          key={charge.id}
                          amount={charge.amount}
                          currency={booking.currency}
                          label={
                            charge.inclusion?.description ?? "Additional charge"
                          }
                        />
                      );
                    })}
                  {purchasableExtraCharges.map((extra) => {
                    return (
                      <TooltipLineItem
                        key={extra.id}
                        amount={extra.amount}
                        currency={booking.currency}
                        label={
                          extra.purchasableExtra?.supplierPurchasableExtra
                            .name ?? "--"
                        }
                      />
                    );
                  })}
                  <TooltipLineItem
                    amount={booking.charge_summary.supplier_refund_amount}
                    currency={booking.currency}
                    label="Holding deposit refund (supplier)"
                  />
                </div>
              </PopoverContent>
            </Popover>
          </td>
        </tr>
        <tr>
          <th
            scope="row"
            className="pl-0 pr-3 pt-4 text-right text-sm font-semibold text-gray-900"
          >
            Total cost:
          </th>
          <td className="pl-3 pr-0 pt-4 text-right text-sm font-semibold text-gray-900">
            {formatCurrency(
              booking.charge_summary.total_amount,
              booking.currency,
            )}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function TooltipLineItem({
  amount,
  label,
  currency,
}: {
  label?: string;
  amount: number;
  currency: Currency;
}) {
  if (amount === 0 || Number.isNaN(amount)) return null;

  return (
    <div className="flex items-center justify-between">
      <Text
        className="text-xs"
        dangerouslySetInnerHTML={{ __html: label ?? "--" }}
      />
      <span className="font-medium text-gray-900">
        {formatCurrency(amount, currency)}
      </span>
    </div>
  );
}
